const text = value =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const record = value =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

const safeHttpUrl = value => {
  const candidate = text(value);
  if (!candidate) return null;

  try {
    const url = new URL(candidate);
    return ['http:', 'https:'].includes(url.protocol) ? url.toString() : null;
  } catch {
    return null;
  }
};

const channelFromValue = value => {
  const normalized = text(value)?.toLowerCase();
  if (!normalized) return null;
  if (normalized.includes('whatsapp')) return 'whatsapp';
  if (normalized.includes('instagram')) return 'instagram';
  if (normalized.includes('facebook') || normalized.includes('messenger')) {
    return 'messenger';
  }
  return null;
};

const channelFromLabels = labels => {
  if (!Array.isArray(labels)) return null;
  if (labels.includes('whatsapp_ads')) return 'whatsapp';
  if (labels.includes('instagram_ads')) return 'instagram';
  if (labels.includes('messenger_ads')) return 'messenger';
  return null;
};
const getChannel = (conversation, custom, senderCustom) => {
  if (text(custom.meta_ctwa_clid)) return 'whatsapp';

  const byLabel = channelFromLabels(conversation.labels);
  if (byLabel) return byLabel;

  const candidates = [
    conversation.channel_type,
    record(conversation.inbox).channel_type,
    record(conversation.meta).channel_type,
    record(conversation.meta).channel,
    record(record(conversation.meta).sender).channel_type,
    senderCustom.channel_type,
  ];

  return (
    candidates.map(channelFromValue).find(channel => Boolean(channel)) ||
    'unknown'
  );
};

export const getMetaAdContext = conversationValue => {
  const conversation = record(conversationValue);
  const custom = record(conversation.custom_attributes);
  const additional = record(conversation.additional_attributes);
  const referral = record(additional.referral);
  const sender = record(record(conversation.meta).sender);
  const senderCustom = record(sender.custom_attributes);

  const referralIsAd = text(referral.source_type)?.toLowerCase() === 'ad';
  const labelAdId = Array.isArray(conversation.labels)
    ? conversation.labels
        .find(label => typeof label === 'string' && label.startsWith('ad_id.'))
        ?.slice('ad_id.'.length) || null
    : null;
  const adId =
    text(custom.meta_ad_id) ||
    (referralIsAd ? text(referral.source_id) : null) ||
    text(labelAdId);

  if (!adId) return null;

  const referralSource = text(custom.meta_referral_source);
  const ctwaClid = text(custom.meta_ctwa_clid);
  const channel = getChannel(conversation, custom, senderCustom);
  const adAccountId = text(custom.meta_ad_account_id);
  const adsManagerUrl =
    safeHttpUrl(custom.meta_ads_manager_url) ||
    (adAccountId && /^[0-9]+$/.test(adAccountId)
      ? `https://www.facebook.com/adsmanager/manage/ads?act=${encodeURIComponent(
          adAccountId
        )}&selected_ad_ids=${encodeURIComponent(adId)}`
      : null);

  return {
    adId,
    adAccountId,
    adsManagerUrl,
    adName:
      text(custom.meta_ad_name) ||
      text(custom.meta_creative_name) ||
      text(referral.headline),
    adsetId: text(custom.meta_adset_id),
    adsetName: text(custom.meta_adset_name),
    campaignId: text(custom.meta_campaign_id),
    campaignName: text(custom.meta_campaign_name),
    creativeId: text(custom.meta_creative_id),
    creativeName: text(custom.meta_creative_name),
    thumbnailUrl:
      safeHttpUrl(custom.meta_ad_thumbnail_url) ||
      safeHttpUrl(referral.thumbnail_url) ||
      safeHttpUrl(referral.image_url),
    body:
      text(custom.meta_ad_body) ||
      text(custom.meta_creative_body) ||
      text(referral.body),
    title:
      text(custom.meta_ad_title) ||
      text(custom.meta_creative_title) ||
      text(referral.headline),
    linkUrl:
      safeHttpUrl(custom.meta_ad_link_url) ||
      safeHttpUrl(custom.meta_creative_link_url) ||
      safeHttpUrl(referral.source_url),
    imageUrl:
      safeHttpUrl(custom.meta_ad_image_url) ||
      safeHttpUrl(custom.meta_creative_image_url) ||
      safeHttpUrl(referral.image_url),
    objectStoryId:
      text(custom.meta_ad_object_story_id) || text(custom.meta_object_story_id),
    instagramMediaId: text(custom.meta_instagram_media_id),
    referralSource,
    ctwaClid,
    channel,
    attributionQuality:
      referralSource?.toUpperCase() === 'ADS' || ctwaClid || referralIsAd
        ? 'confirmed'
        : 'partial',
    firstTouchAdId: text(senderCustom.first_touch_ad_id),
    firstTouchCampaignId: text(senderCustom.first_touch_campaign_id),
    welcomeText:
      text(custom.meta_ad_welcome_message) ||
      text(referral.page_welcome_message) ||
      text(record(referral.welcome_message).text) ||
      text(referral.welcome_message),
  };
};
export const hasMetaAdContext = conversation =>
  Boolean(getMetaAdContext(conversation));

export const getOrganicReferral = conversationValue => {
  if (hasMetaAdContext(conversationValue)) return null;

  const conversation = record(conversationValue);
  const referral = record(record(conversation.additional_attributes).referral);

  return text(referral.source_type)?.toLowerCase() === 'post' ? referral : null;
};
